import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const SCROLL_PIN_THRESHOLD = 12;

function FloatBackButton({ onClick, label = 'Back', ariaLabel, variant = 'float' }) {
  const isInline = variant === 'inline';
  const wrapperRef = useRef(null);
  const [isPinned, setIsPinned] = useState(false);
  const [anchor, setAnchor] = useState({ left: 24, top: 36 });

  const updateAnchor = useCallback(() => {
    if (isInline) return;
    const el = wrapperRef.current;
    if (!el) return;

    const { left, top } = el.getBoundingClientRect();
    if (window.scrollY <= SCROLL_PIN_THRESHOLD) {
      setAnchor({ left, top });
      return;
    }

    setAnchor((prev) => ({ ...prev, left }));
  }, [isInline]);

  useEffect(() => {
    if (isInline) return undefined;

    const handleScroll = () => {
      setIsPinned(window.scrollY > SCROLL_PIN_THRESHOLD);
      if (window.scrollY <= SCROLL_PIN_THRESHOLD) {
        updateAnchor();
      }
    };

    const handleResize = () => updateAnchor();

    handleScroll();
    updateAnchor();
    requestAnimationFrame(updateAnchor);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    const el = wrapperRef.current;
    const resizeObserver = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateAnchor) : null;
    if (resizeObserver && el) {
      resizeObserver.observe(el);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [updateAnchor, isInline]);

  const pinnedStyle = !isInline && isPinned
    ? {
        position: 'fixed',
        top: anchor.top,
        left: anchor.left,
        zIndex: 35,
      }
    : undefined;

  return (
    <div
      ref={wrapperRef}
      className={`hp-floatback${isInline ? ' hp-floatback--inline' : ''}${isPinned ? ' hp-floatback--pinned' : ''}`}
    >
      <button
        type='button'
        className={`hp-floatback__btn${!isInline && !isPinned ? ' hp-floatback__btn--expanded' : ''}`}
        style={pinnedStyle}
        aria-label={ariaLabel || label}
        onClick={onClick}
      >
        <svg
          width='17'
          height='17'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.9'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <path d='M19 12H5M12 5l-7 7 7 7' />
        </svg>
        <span className='hp-floatback__label'>{label}</span>
      </button>
    </div>
  );
}

FloatBackButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  variant: PropTypes.oneOf(['float', 'inline']),
};

FloatBackButton.defaultProps = {
  label: 'Back',
  ariaLabel: undefined,
  variant: 'float',
};

export default FloatBackButton;
